import { CANNON_CATALOG, type PvpAppearance } from '@kenocyber/shared';
import {
  DASH_LATERAL_SCALE,
  DASH_TEXTURE_HEIGHT,
  DASH_TEXTURE_WIDTH,
  dashMaxHalfThickness,
  dashPaintedExtent,
  HUNT_BOLT_THICKNESS_PER_HULL,
} from '../game/projectile-art';

/**
 * What a duelling SHOT looks like, as arithmetic.
 *
 * The sibling of `ship-art.ts`, for the other half of what a participant puts
 * on screen. Everything here is a pure function of the SERVER's profile and the
 * SERVER's numbers: no Phaser, no canvas, no inventory, no local role.
 *
 * ## Reused, not re-derived
 *
 * The artwork is the hunt's. `game/projectile-art.ts` bakes the neon dash - the
 * near-white core, the saturated body, the hot leading section and the two
 * haloes around them - and a duel draws exactly that texture, from exactly that
 * generator, at exactly the same proportions. A duel that painted its own bolt
 * would drift from the hunt the first time either moved, and a player would
 * notice their own gun changing shape between modes.
 *
 * What this module adds is the two things a duel genuinely decides for itself:
 * WHOSE colour a shot is, and how large the sprite has to be for its lit width
 * to say what the collision radius is.
 */

/* -------------------------------------------------------------------------- */
/*  Colour                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The colour of a shot nobody could be identified as the owner of.
 *
 * Reached when a projectile's seat is absent from `arena.appearance`, when the
 * profile is there but carries no cannon, or when it names a rarity the
 * catalogue has no colour for. All three are real states rather than errors: a
 * brand new account has a hull and no gun and is allowed into a duel, and a
 * frame can legitimately arrive a tick before the welcome that describes a
 * participant.
 *
 * ## Why a colour rather than nothing
 *
 * Because the alternative is hiding an authoritative projectile, and a shot the
 * player cannot see is a rule they cannot play against. It keeps the core, the
 * position, the direction and the full lit width the hitbox demands - only the
 * hue is a stand-in.
 *
 * ## Why THIS colour
 *
 * Pale steel blue: deliberately none of the three rarities that exist
 * (Excellent `#2F9BFF`, Perfect `#FFE81F`, Anomaly `#FF2222`) and not Prismatic
 * `#FF1F5A` either, so a fallback can never be mistaken for a gun somebody is
 * actually holding. Desaturated enough to read as "unknown" beside a neon bolt,
 * bright enough to survive the arena's own glow at minimum zoom.
 */
export const PVP_FALLBACK_PROJECTILE_COLOUR = 0xbfd8ff;

/**
 * `#rrggbb` to the integer Phaser wants, without Phaser.
 *
 * `Phaser.Display.Color.HexStringToColor` is what `HuntScene` uses, and it
 * cannot be reached from a module that must stay testable outside a WebGL
 * runtime. The catalogue's own values are the only input this ever sees and
 * `catalog.test.ts` pins their shape, so anything malformed is a broken build
 * rather than a case to be lenient about - it takes the fallback.
 */
function hexColour(value: string): number | null {
  const match = /^#([0-9a-fA-F]{6})$/.exec(value.trim());
  if (!match?.[1]) {
    return null;
  }
  return Number.parseInt(match[1], 16);
}

/**
 * The colour a participant's shots are, from the cannon the SERVER gave them.
 *
 * The chain is `projectile.seat` -> `arena.appearance[seat]` -> `.cannon` ->
 * `CANNON_CATALOG[...].projectileColor`, and every link is authoritative. It is
 * deliberately NOT reachable from the local player's role: Excellent's blue,
 * Perfect's yellow and Anomaly's red belong to the equipment, and repainting
 * them by ally or enemy would tell a player their opponent had a gun they do
 * not have.
 */
export function pvpProjectileColour(appearance: PvpAppearance | null | undefined): number {
  const rarity = appearance?.cannon;
  if (!rarity) {
    return PVP_FALLBACK_PROJECTILE_COLOUR;
  }
  const entry = CANNON_CATALOG[rarity];
  if (!entry) {
    return PVP_FALLBACK_PROJECTILE_COLOUR;
  }
  return hexColour(entry.projectileColor) ?? PVP_FALLBACK_PROJECTILE_COLOUR;
}

/** True when a colour is the stand-in rather than a cannon's own. */
export function pvpProjectileColourIsFallback(colour: number): boolean {
  return colour === PVP_FALLBACK_PROJECTILE_COLOUR;
}

/* -------------------------------------------------------------------------- */
/*  Scale                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * How a dash is drawn for a given collision radius, in world units.
 *
 * `scaleX` and `scaleY` go straight to `setScale`; the rest are what those two
 * numbers MEAN on screen, so a test can assert the picture rather than the
 * multiplier.
 */
export interface PvpDashGauge {
  /** Along the sprite's own length. */
  readonly scaleX: number;
  /** Across it. Always the larger: the artwork is wider than it is long-ish. */
  readonly scaleY: number;
  /** Lit width, tip to tip across the flight path, in world units. */
  readonly paintedThickness: number;
  /** How far the paint reaches AHEAD of the authoritative point. */
  readonly forwardReach: number;
  /** How far it trails behind it. */
  readonly tailReach: number;
  /** Total drawn length, front to back. */
  readonly paintedLength: number;
}

/**
 * The scale that makes a dash say what the hitbox is.
 *
 * ## The rule
 *
 * A duel's projectile collides as a CIRCLE of `projectileRadius`, and that is
 * the number the player is dodging. So the lit part of the sprite, measured
 * across its flight path, must be at least the collision DIAMETER:
 *
 *     2 * dashMaxHalfThickness() * scaleY  >=  2 * projectileRadius
 *
 * This returns the scale where that holds with equality - the smallest drawing
 * that tells the truth. Anything larger would claim area the simulation does
 * not have.
 *
 * ## Why the length follows rather than leads
 *
 * `scaleX = scaleY / DASH_LATERAL_SCALE` is exactly the proportion the hunt
 * draws its own bolts at (`DASH_RENDER_SCALE` by `DASH_RENDER_SCALE *
 * DASH_LATERAL_SCALE`). So a duel's shot is the hunt's shot MAGNIFIED, not a
 * differently shaped object that happens to share a texture: same aspect, same
 * taper, same core-to-halo ratio, same everything except how many world units a
 * texture pixel covers. At the shipped radius of 7 the magnification is 2.34x,
 * which is what it takes for a bolt to read as 14 units wide.
 *
 * ## Why the tip may not simply be scaled up too
 *
 * The origin sits at 0.874 of the texture, so almost the whole bolt is BEHIND
 * the authoritative point and only a sliver reaches past it - 1.04 authored
 * pixels, 1.22 world units at the shipped radius, against a collision radius of
 * 7. That is the asymmetry the artwork was designed with and it is the honest
 * one for a duel: the tail is energy and direction, and light in front of the
 * point is the only part that could ever be mistaken for reach. `forwardReach`
 * is returned so a test can hold it under the radius rather than trust this
 * paragraph.
 */
export function pvpDashGauge(projectileRadius: number, hullDiameter: number): PvpDashGauge {
  const half = dashMaxHalfThickness();
  const extent = dashPaintedExtent();
  if (
    !Number.isFinite(projectileRadius) ||
    projectileRadius <= 0 ||
    !Number.isFinite(hullDiameter) ||
    hullDiameter <= 0 ||
    half <= 0
  ) {
    // A radius the welcome never sent is a broken frame, not a reason to draw a
    // shot at zero: the artwork's own authored proportions are the fallback.
    return {
      scaleX: 1,
      scaleY: DASH_LATERAL_SCALE,
      paintedThickness: 2 * half * DASH_LATERAL_SCALE,
      forwardReach: extent.forward,
      tailReach: extent.tail,
      paintedLength: extent.length,
    };
  }
  /*
   * The HUNT's proportion, applied to a duel hull (gate V27A).
   *
   * This used to be `projectileRadius / (DASH_LATERAL_SCALE * half)` - the
   * scale at which the painted bolt is exactly the collision DIAMETER. The
   * reasoning above it was that anything smaller claims less area than the
   * simulation has, and the shot a player dodges should be the shot they see.
   *
   * Measured against the mode this game already had, that produced a bolt more
   * than four times too big:
   *
   * ```
   *          hull    painted    painted / hull
   * hunt     96 u     5.98 u        0.0623
   * duel     52 u    14.00 u        0.2692     4.32x
   * ```
   *
   * And the premise does not survive the comparison. The hunt's own shot
   * collides at `hitRadius` 10 - a 20-unit circle against a 96-unit hull - and
   * is drawn 5.98 units thick, which is under a third of it. Drawing the hitbox
   * was never this game's grammar; it was a rule one mode adopted alone, and
   * the oversize is exactly what it bought.
   *
   * So the duel takes the hunt's ratio and magnifies nothing: same texture,
   * same aspect, same taper, and the same fraction of its own hull. The
   * collision radius, the speed, the cadence and the origin are untouched -
   * this function has never been read by anything that decides a hit.
   */
  const scaleX = (HUNT_BOLT_THICKNESS_PER_HULL * hullDiameter) / (2 * half * DASH_LATERAL_SCALE);
  const scaleY = scaleX * DASH_LATERAL_SCALE;
  return {
    scaleX,
    scaleY,
    paintedThickness: 2 * half * scaleY,
    forwardReach: extent.forward * scaleX,
    tailReach: extent.tail * scaleX,
    paintedLength: extent.length * scaleX,
  };
}

/**
 * The trail streak's scale, tied to the bolt rather than chosen beside it.
 *
 * The hunt draws its wake at `STREAK_RENDER_SCALE` against a bolt at
 * `DASH_RENDER_SCALE` - the same figure, which is what keeps the residue in
 * proportion to the shot. A duel magnifies both by the same amount for the same
 * reason, so the relation is written as the ratio rather than as a second
 * constant that would eventually disagree.
 */
export function pvpStreakScale(gauge: PvpDashGauge): number {
  return gauge.scaleX;
}

/**
 * The authored texture, restated so a browser test can check what it drew.
 *
 * Not used to size anything - {@link pvpDashGauge} does that from the painted
 * extent, not from the texture's bounds - but a spec that reads `displayWidth`
 * off a sprite needs to know what it is a multiple of.
 */
export const PVP_DASH_TEXTURE_SIZE = Object.freeze({
  width: DASH_TEXTURE_WIDTH,
  height: DASH_TEXTURE_HEIGHT,
});

/**
 * **Quanto um tiro de CADÊNCIA é desenhado maior** (decisão do dono, V28H.X).
 *
 * Quatro vezes ao comprido e quase o dobro de espessura, em duas passagens: o
 * dono viu o primeiro aumento — dobro e metade — e pediu outro por cima. Os
 * números são o resultado das duas contas, 2 x 2 e 1,5 x 1,3.
 *
 * Moradas aqui e não numa cena (gate V28I.CF.3): o duelo e o mundo aberto
 * desenham o mesmo tiro, e dois literais iguais em dois ficheiros seriam duas
 * coisas que podem divergir. A caça não pediu este aumento e não o usa.
 *
 * Só o DESENHO. O raio de colisão é da sala.
 */
export const PVP_CADENCE_SHOT_LENGTH = 4;
export const PVP_CADENCE_SHOT_THICKNESS = 1.95;

/**
 * **O rasto de um tiro de arena**: a cadência de emissão, a vida, a opacidade e
 * o tecto de partículas vivas. Partilhados pelo duelo e pelo mundo aberto.
 *
 * Um em cada três quadros, e com vida mais curta que a da caça, porque noventa
 * tiros a emitir em todos os quadros seriam cinco mil partículas por segundo
 * para uma coisa cujo único trabalho é ser ténue. O tecto só pode largar
 * RESÍDUO: os tiros, as naves e as barras nunca são reduzidos.
 */
export const PROJECTILE_TRAIL_EVERY_FRAMES = 3;
export const PROJECTILE_TRAIL_LIFESPAN_MS = Object.freeze({ min: 90, max: 150 });
export const PROJECTILE_TRAIL_ALPHA = Object.freeze({ start: 0.42, end: 0 });
export const PROJECTILE_TRAIL_MAX = 320;
